import React, { useEffect, useMemo, useState } from "react";
import { exportOrder } from "@/lib/exportOrder";
import { api } from "@/lib/api";
import { useTableSort } from "@/lib/tableSort";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Download, FileText, Search, Loader2, Truck, Users } from "lucide-react";

const NAVY = "#0F2C59";
const GREEN = "#16A34A";
const AMBER = "#D97706";

const STAGE_CLS = {
  alindi: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  merkezde: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
  gorevlide: "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300",
  sandikta: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
};

export const DocumentDelivery = ({ committeeNo = "all" }) => {
  const [data, setData] = useState(null);
  const [committee, setCommittee] = useState(committeeNo || "all");
  useEffect(() => { setCommittee(committeeNo || "all"); }, [committeeNo]);
  const [stage, setStage] = useState("all");
  const [q, setQ] = useState("");
  const [reload, setReload] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [edit, setEdit] = useState(null);
  const [carrier, setCarrier] = useState("");
  const [note, setNote] = useState("");
  const [newStage, setNewStage] = useState("alindi");
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkCarrier, setBulkCarrier] = useState("");
  const [bulkStage, setBulkStage] = useState("keep");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [view, setView] = useState("firms");
  const [byCarrier, setByCarrier] = useState(null);
  const [receiptBusy, setReceiptBusy] = useState("");

  useEffect(() => {
    setData(null);
    const params = committee !== "all" ? { committee_no: Number(committee) } : {};
    api.get("/reports/document-delivery", { params })
      .then(({ data }) => setData(data))
      .catch(() => { setData({ rows: [], stages: [] }); toast.error("Teslim takibi yüklenemedi"); });
  }, [committee, reload]);

  useEffect(() => {
    if (view !== "carriers") return;
    setByCarrier(null);
    const params = committee !== "all" ? { committee_no: Number(committee) } : {};
    api.get("/reports/document-delivery/by-carrier", { params })
      .then(({ data }) => setByCarrier(data))
      .catch(() => toast.error("Görevli listesi yüklenemedi"));
  }, [view, committee, reload]);

  const receipt = async (carrier) => {
    setReceiptBusy(carrier || "all");
    try {
      const params = { ...(carrier ? { carrier } : {}), ...(committee !== "all" ? { committee_no: Number(committee) } : {}) };
      const res = await api.get("/reports/document-delivery/receipt", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `teslim_tutanagi${carrier ? "_" + carrier.replace(/\s+/g, "_") : ""}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tutanak oluşturulamadı");
    } finally { setReceiptBusy(""); }
  };

  const rows = useMemo(() => {
    const term = q.trim().toLocaleLowerCase("tr");
    return (data?.rows || []).filter((r) => {
      if (stage !== "all" && r.delivery_stage !== stage) return false;
      if (stage === "carrier_missing" && r.ballot_carrier) return false;
      if (!term) return true;
      return (r.company_name || "").toLocaleLowerCase("tr").includes(term)
        || (r.document_holder || "").toLocaleLowerCase("tr").includes(term)
        || (r.ballot_carrier || "").toLocaleLowerCase("tr").includes(term)
        || String(r.sicil_no || "").includes(term);
    });
  }, [data, q, stage]);
  const dT = useTableSort(rows);
  const allSelected = rows.length > 0 && selected.length === rows.length;
  const toggleAll = () => setSelected(allSelected ? [] : rows.map((r) => r.id));
  const toggleOne = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const bulkAssign = async () => {
    if (!bulkCarrier.trim() && bulkStage === "keep") {
      toast.error("Görevli adı girin veya aşama seçin");
      return;
    }
    setBulkBusy(true);
    try {
      const { data: res } = await api.post("/reports/document-delivery/bulk", {
        voter_ids: selected,
        ...(bulkCarrier.trim() ? { ballot_carrier: bulkCarrier.trim() } : {}),
        ...(bulkStage !== "keep" ? { delivery_stage: bulkStage } : {}),
      });
      toast.success(`${res.updated} firma güncellendi`);
      setBulkOpen(false); setSelected([]); setBulkCarrier(""); setBulkStage("keep");
      setReload((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toplu atama başarısız");
    } finally { setBulkBusy(false); }
  };

  const doExport = async (fmt) => {
    setExporting(true);
    try {
      const params = { format: fmt, only: 1, order: await exportOrder(dT.sorted.map((r) => r.id)),
        ...(committee !== "all" ? { committee_no: Number(committee) } : {}) };
      const res = await api.get("/reports/document-delivery/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `belge_teslim_takibi.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error("İndirilemedi");
    } finally { setExporting(false); }
  };

  const openEdit = (r) => {
    setEdit(r);
    setCarrier(r.ballot_carrier || "");
    setNote(r.delivery_note || "");
    setNewStage(r.delivery_stage || "alindi");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/voters/${edit.id}`, {
        delivery_stage: newStage,
        ...(carrier.trim() !== (edit.ballot_carrier || "") ? { ballot_carrier: carrier.trim() } : {}),
        ...(note.trim() !== (edit.delivery_note || "") ? { delivery_note: note.trim() } : {}),
      });
      toast.success("Teslim durumu güncellendi");
      setEdit(null);
      setReload((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncellenemedi");
    } finally { setSaving(false); }
  };

  const advance = async (r) => {
    const keys = ["alindi", "merkezde", "gorevlide", "sandikta"];
    const next = keys[Math.min(keys.indexOf(r.delivery_stage) + 1, keys.length - 1)];
    if (next === r.delivery_stage) return;
    if (next === "gorevlide" && !r.ballot_carrier) {
      openEdit(r);
      toast.info("Bu aşama için sandığa götürecek kişiyi girin");
      return;
    }
    try {
      await api.patch(`/voters/${r.id}`, { delivery_stage: next });
      toast.success("Bir sonraki aşamaya alındı");
      setReload((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncellenemedi");
    }
  };

  if (!data) return <Skeleton className="h-80 w-full" />;

  return (
    <div className="space-y-3" data-testid="document-delivery">
      <div className="inline-flex rounded-lg border border-border overflow-hidden" data-testid="dd-view-toggle">
        <button type="button" onClick={() => setView("firms")}
          className={`px-3 py-1.5 text-xs font-medium ${view === "firms" ? "bg-[#0F2C59] text-white" : "hover:bg-muted"}`} data-testid="dd-view-firms">Firma Bazlı</button>
        <button type="button" onClick={() => setView("carriers")}
          className={`px-3 py-1.5 text-xs font-medium ${view === "carriers" ? "bg-violet-600 text-white" : "hover:bg-muted"}`} data-testid="dd-view-carriers">Görevli Bazlı</button>
      </div>

      {view === "carriers" ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Select value={committee} onValueChange={setCommittee}>
              <SelectTrigger className="h-9 w-56" data-testid="dd-carrier-committee"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">Tüm komiteler</SelectItem>
                {(data.committees || []).map((c) => (
                  <SelectItem key={c.committee_no} value={String(c.committee_no)}>{c.committee_no}. {c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" className="h-9 ml-auto bg-[#0F2C59] hover:bg-[#0F2C59]/90"
              disabled={receiptBusy === "all" || !byCarrier?.carriers?.length} onClick={() => receipt(null)} data-testid="dd-receipt-all">
              {receiptBusy === "all" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileText className="h-4 w-4 mr-1.5" />Tüm Tutanaklar (PDF)</>}
            </Button>
          </div>
          {!byCarrier ? <Skeleton className="h-64 w-full" /> : (
            <div className="grid gap-3 md:grid-cols-2">
              {byCarrier.carriers.map((g) => (
                <Card key={g.carrier} className="p-4 space-y-2" data-testid={`dd-carrier-card-${g.carrier}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-sm truncate">{g.carrier}</p>
                      <p className="text-xs text-muted-foreground">
                        {g.count} belge · {g.delivered} sandığa götürüldü
                      </p>
                    </div>
                    <Button variant="outline" size="sm" className="h-8 shrink-0" disabled={receiptBusy === g.carrier}
                      onClick={() => receipt(g.carrier)} data-testid={`dd-receipt-${g.carrier}`}>
                      {receiptBusy === g.carrier ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileText className="h-4 w-4 mr-1.5" />Tutanak</>}
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {g.committees.map((c) => (
                      <span key={c.committee_no} className="text-[11px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                        #{c.committee_no} · {c.count}
                      </span>
                    ))}
                  </div>
                  <ul className="text-xs text-muted-foreground space-y-0.5 max-h-36 overflow-y-auto">
                    {g.items.slice(0, 40).map((it) => (
                      <li key={it.id} className="truncate">• {it.company_name}</li>
                    ))}
                    {g.items.length > 40 && <li className="italic">…ve {g.items.length - 40} firma daha</li>}
                  </ul>
                </Card>
              ))}
            </div>
          )}
        </div>
      ) : (
      <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {(data.stages || []).map((s) => (
          <Card key={s.key} className="p-3 cursor-pointer hover:border-[#0F2C59]/40 transition-colors"
            onClick={() => setStage(stage === s.key ? "all" : s.key)} data-testid={`dd-stage-kpi-${s.key}`}>
            <div className="text-xl font-bold tabular-nums" style={{ color: s.key === "sandikta" ? GREEN : NAVY }}>{s.count}</div>
            <div className="text-xs text-muted-foreground">{s.label}</div>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="px-2 py-1 rounded-md bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30" data-testid="dd-holder-missing">
          Belge sahibi girilmemiş: <b>{data.holder_missing}</b>
        </span>
        <span className="px-2 py-1 rounded-md bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30" data-testid="dd-carrier-missing">
          Sandığa götürecek kişi girilmemiş: <b>{data.carrier_missing}</b>
        </span>
        <span className="px-2 py-1 rounded-md bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30" data-testid="dd-done">
          Sandığa götürüldü: <b>{data.done}</b> / {data.total}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={committee} onValueChange={setCommittee}>
          <SelectTrigger className="h-9 w-56" data-testid="dd-committee"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">Tüm komiteler</SelectItem>
            {(data.committees || []).map((c) => (
              <SelectItem key={c.committee_no} value={String(c.committee_no)}>{c.committee_no}. {c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={stage} onValueChange={setStage}>
          <SelectTrigger className="h-9 w-56" data-testid="dd-stage-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm aşamalar</SelectItem>
            {(data.stages || []).map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            <SelectItem value="carrier_missing">Götürecek kişi girilmemiş</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Firma, belge sahibi veya götürecek kişi ara..." value={q}
            onChange={(e) => setQ(e.target.value)} data-testid="dd-search" />
        </div>
        <Button variant="outline" size="sm" className="h-9" disabled={exporting || !rows.length} onClick={() => doExport("xlsx")} data-testid="dd-export-xlsx">
          <Download className="h-4 w-4 mr-1.5" />Excel
        </Button>
        <Button variant="outline" size="sm" className="h-9" disabled={exporting || !rows.length} onClick={() => doExport("pdf")} data-testid="dd-export-pdf">
          <FileText className="h-4 w-4 mr-1.5" />PDF
        </Button>
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#0F2C59]/30 bg-[#0F2C59]/5 px-3 py-2" data-testid="dd-bulk-bar">
          <span className="text-sm font-medium" data-testid="dd-selected-count">{selected.length} firma seçildi</span>
          <Button size="sm" className="h-8 bg-[#0F2C59] hover:bg-[#0F2C59]/90" onClick={() => setBulkOpen(true)} data-testid="dd-bulk-open">
            <Users className="h-4 w-4 mr-1.5" />Toplu Görevli Ata
          </Button>
          <Button variant="ghost" size="sm" className="h-8" onClick={() => setSelected([])} data-testid="dd-bulk-clear">Seçimi temizle</Button>
        </div>
      )}

      <Card className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="dd-table">
          <thead className="text-muted-foreground border-b border-border bg-muted/40">
            <tr>
              <th className="w-10 py-2 px-3">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="dd-select-all" aria-label="Tümünü seç" />
              </th>
              <dT.Th sortKey="dd-firma" get={(r) => r.company_name} text className="text-left font-medium py-2 px-3">Firma</dT.Th>
              <dT.Th sortKey="dd-komite" get={(r) => r.committee_no} className="text-left font-medium py-2 px-3">Komite / Şube</dT.Th>
              <dT.Th sortKey="dd-asama" get={(r) => r.stage_no} className="text-left font-medium py-2 px-3">Teslim Aşaması</dT.Th>
              <dT.Th sortKey="dd-sahip" get={(r) => r.document_holder} text className="text-left font-medium py-2 px-3">Belge Kimde</dT.Th>
              <dT.Th sortKey="dd-yetkili" get={(r) => r.voting_person} text className="text-left font-medium py-2 px-3">Oy Kullanacak Yetkili</dT.Th>
              <th className="text-right font-medium py-2 px-3">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {dT.sorted.map((r) => (
              <tr key={r.id} className="border-b border-border/40 hover:bg-muted/20" data-testid={`dd-row-${r.id}`}>
                <td className="py-2 px-3">
                  <Checkbox checked={selected.includes(r.id)} onCheckedChange={() => toggleOne(r.id)} data-testid={`dd-select-${r.id}`} aria-label="Firma seç" />
                </td>
                <td className="py-2 px-3 font-medium">{r.company_name}</td>
                <td className="py-2 px-3 text-muted-foreground whitespace-nowrap">#{r.committee_no} · {r.branch}</td>
                <td className="py-2 px-3">
                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${STAGE_CLS[r.delivery_stage]}`} data-testid={`dd-stage-${r.id}`}>
                    {r.stage_label}
                  </span>
                </td>
                <td className="py-2 px-3">{r.document_holder || <span className="text-amber-600">girilmedi</span>}</td>
                <td className="py-2 px-3">{r.voting_person || <span className="text-amber-600">girilmedi</span>}</td>
                <td className="py-2 px-3 text-right whitespace-nowrap">
                  {r.delivery_stage !== "sandikta" && (
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-emerald-600" title="Bir sonraki aşamaya al"
                      onClick={() => advance(r)} data-testid={`dd-advance-${r.id}`}>
                      <Truck className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button variant="outline" size="sm" className="h-7 px-2 ml-1" onClick={() => openEdit(r)} data-testid={`dd-edit-${r.id}`}>
                    Düzenle
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground" data-testid="dd-empty">Kayıt bulunamadı</p>}
      </Card>
      <p className="text-xs text-muted-foreground">
        İpucu: komite veya aşama filtresi uygulayıp başlıktaki kutuyu işaretleyerek o gruptaki tüm firmalara tek seferde görevli atayabilirsiniz.
      </p>
      </>
      )}

      <Dialog open={bulkOpen} onOpenChange={(o) => !o && setBulkOpen(false)}>
        <DialogContent className="max-w-md" data-testid="dd-bulk-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Toplu görevli ata</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Seçilen <b>{selected.length}</b> firmanın belgesi için sandığa götürecek kişiyi ve/veya teslim aşamasını topluca güncelle.
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs">Sandığa götürecek kişi</Label>
              <Input value={bulkCarrier} onChange={(e) => setBulkCarrier(e.target.value)} placeholder="Ad Soyad" data-testid="dd-bulk-carrier" />
              {(data.carriers || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {(data.carriers || []).slice(0, 8).map((c) => (
                    <Button key={c} type="button" variant={bulkCarrier === c ? "default" : "outline"} size="sm"
                      className="h-7 text-xs" onClick={() => setBulkCarrier(c)} data-testid={`dd-bulk-carrier-opt-${c}`}>{c}</Button>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Teslim aşaması</Label>
              <Select value={bulkStage} onValueChange={setBulkStage}>
                <SelectTrigger className="h-9" data-testid="dd-bulk-stage"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="keep">Aşamayı değiştirme</SelectItem>
                  {(data.stages || []).map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setBulkOpen(false)} data-testid="dd-bulk-cancel">Vazgeç</Button>
              <Button size="sm" className="bg-[#0F2C59] hover:bg-[#0F2C59]/90" onClick={bulkAssign} disabled={bulkBusy} data-testid="dd-bulk-save">
                {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : `${selected.length} firmaya uygula`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-md" data-testid="dd-edit-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Belge teslim durumu</DialogTitle>
          </DialogHeader>
          {edit && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">{edit.company_name}</p>
              <div className="text-xs rounded-md border border-border p-2 space-y-0.5">
                <p>Belge kimde: <b>{edit.document_holder || "girilmedi"}</b></p>
                <p>Oy kullanacak yetkili: <b>{edit.voting_person || "girilmedi"}</b></p>
                <p className="text-muted-foreground">Belge tarihi: {edit.proxy_document_date || "—"}</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Teslim aşaması</Label>
                <Select value={newStage} onValueChange={setNewStage}>
                  <SelectTrigger className="h-9" data-testid="dd-edit-stage"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(data.stages || []).map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Sandığa götürecek kişi</Label>
                <Input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="Ad Soyad" data-testid="dd-edit-carrier" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Not</Label>
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Opsiyonel" data-testid="dd-edit-note" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setEdit(null)} data-testid="dd-edit-cancel">Vazgeç</Button>
                <Button size="sm" className="bg-[#0F2C59] hover:bg-[#0F2C59]/90" onClick={save} disabled={saving} data-testid="dd-edit-save">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentDelivery;
